import { createContainerBreakpoints } from "$root/components/container-breakpoints";
import { Icon, type IconName, type Option, Row, Text } from "@mdotm/shadcn/legacy/components";
import { Button, type ButtonProps, type DropdownTriggerProps } from "@mdotm/shadcn/mdotm/components";
import { Popover, PopoverContent, PopoverTrigger } from "@mdotm/shadcn/ui";
import React, { type ReactNode, type RefObject, forwardRef, useState } from "react";
import { EmptyIcon } from "../../../icons/EmptyIcon";
import type { CompositionGroupBy } from "../../../state";
import { InvestmentPickerButton, type InvestmentPickerButtonProps } from "../../investment-picker";
import { DecimalStepperStacked } from "./DecimalControl";
import { GroupSelectsStacked } from "./GroupControl";
import { CollapsibleSearch } from "$root/components/CollapsibleSearch";
import { Divider } from "$root/components/Divider";

/**
 * The only responsive concern left in this header: the Group-by / Decimal-precision buttons carry text
 * labels until the widget is too narrow to fit them, then collapse to icon-only. `760` is the width
 * (browser-calibrated for this widget's x-small footprints) at and above which the labels still fit.
 */
const { Breakpoints, useAtLeast } = createContainerBreakpoints({ lg: 760, "2xs": 0 });

type CompositionToolbarProps = {
	groupBy: CompositionGroupBy;
	setGroupBy: (v: CompositionGroupBy) => void;
	groupByOptions: Option<string>[];
	query: string;
	onQueryChange: (next: string) => void;
	/** Loads investments into the lab — wired to the same picker used by the page header. */
	onAddPortfolio: InvestmentPickerButtonProps["onSubmitAsync"];
	/** Clears the whole lab (removes every loaded composition). The caller confirms before calling. */
	onResetLab: () => void;
	/** Widget root, whose measured width decides whether the button labels still fit. */
	widgetRef: RefObject<HTMLDivElement | null>;
};

/** Which popover (if any) is currently open — single-open across the toolbar. */
type PopoverId = "group" | "decimal";

/**
 * The Composition widget header: title + global instrument search on the left, then the Group-by and
 * Decimal-precision popover buttons and the "add portfolio" picker on the right. Fixed layout — the only
 * width-driven change is the two buttons dropping their labels (→ icon-only) when the widget is narrow.
 */
export function CompositionEditorToolbar(props: CompositionToolbarProps): React.JSX.Element {
	return (
		<Breakpoints track={props.widgetRef}>
			<CompositionToolbarRow {...props} />
		</Breakpoints>
	);
}

function CompositionToolbarRow({
	groupBy,
	setGroupBy,
	groupByOptions,
	query,
	onQueryChange,
	onAddPortfolio,
	onResetLab,
}: Omit<CompositionToolbarProps, "widgetRef">): React.JSX.Element {
	const showLabels = useAtLeast("lg");
	const [openPopover, setOpenPopover] = useState<PopoverId | null>(null);
	/**
	 * Single-open across the toolbar. Opening sets state to this id (closing any other); closing only
	 * clears when this id is still the open one, so a stale close from the previously-open popover can't
	 * wipe the popover that was just opened in the same tick.
	 */
	const onOpenChange =
		(id: PopoverId) =>
		(next: boolean): void =>
			setOpenPopover((cur) => (next ? id : cur === id ? null : cur));

	const groupProps = { groupBy, setGroupBy, groupByOptions };

	return (
		<Row minSize="0" alignItems="center" className="w-full">
			<Text type="Body/XL/Bold" title="Composition" className="truncate mr-1.5 shrink-0">
				Composition
			</Text>
			<CollapsibleSearch
				query={query}
				onQueryChange={onQueryChange}
				label="Search instruments"
				placeholder="Search instruments…"
				timeout={0}
			/>
			<div className="flex-1" />
			<Row alignItems="center" gap={8} className="shrink-0" justifyContent="flex-end">
				<ToolbarPopoverButton
					icon="Tags"
					label="Group by"
					showLabel={showLabels}
					open={openPopover === "group"}
					onOpenChange={onOpenChange("group")}
				>
					<GroupSelectsStacked {...groupProps} />
				</ToolbarPopoverButton>
				<ToolbarPopoverButton
					icon="List-Numbers"
					label="Decimal precision"
					showLabel={showLabels}
					open={openPopover === "decimal"}
					onOpenChange={onOpenChange("decimal")}
				>
					<DecimalStepperStacked />
				</ToolbarPopoverButton>
				<Divider direction="vertical" className="h-6 shrink-0" />
				<Button
					palette="ghost"
					size="x-small"
					className="shrink-0"
					aria-label="Clear lab"
					title="Clear lab"
					data-qualifier="PortfolioLab/ClearLab/Button"
					onClick={onResetLab}
				>
					<EmptyIcon size={16} />
				</Button>
				<InvestmentPickerButton onSubmitAsync={onAddPortfolio} Trigger={AddPortfolioToolbarTrigger} />
			</Row>
		</Row>
	);
}

/**
 * `Button` exposes its DOM node via `innerRef` (not a forwarded `ref`), so this shim bridges the `ref`
 * that Radix's `PopoverTrigger asChild` injects onto the legacy button — the popover needs it to anchor.
 */
const PopoverTriggerButton = forwardRef<HTMLButtonElement, ButtonProps>(function PopoverTriggerButton(props, ref) {
	return <Button innerRef={ref} {...props} />;
});

/**
 * A ghost x-small button that opens `children` in a popover anchored beneath it. Shows `icon` + `label`,
 * dropping to icon-only when `showLabel` is false (the label stays as the accessible name either way, and
 * doubles as the hover tooltip while the text is hidden).
 * Open state is lifted to the toolbar so only one popover is open at a time; dismisses on outside-click and Esc.
 *
 * Built on the modern Radix `Popover` (not legacy `FloatingContent`) so it composes with the Radix `Select`s
 * inside it: `modal={false}` keeps the background interactive, and Radix's nested dismissable-layer stack
 * recognises the inner select's portal as a branch of this popover, so picking a select option no longer
 * counts as an outside-click and tears the popover (and the in-flight selection) down.
 */
function ToolbarPopoverButton({
	icon,
	label,
	showLabel,
	open,
	onOpenChange,
	children,
}: {
	icon: IconName;
	label: string;
	showLabel: boolean;
	open: boolean;
	onOpenChange: (next: boolean) => void;
	children: ReactNode;
}): React.JSX.Element {
	return (
		<Popover modal={false} open={open} onOpenChange={onOpenChange}>
			<PopoverTrigger asChild>
				<PopoverTriggerButton
					palette="ghost"
					size="x-small"
					className={showLabel ? "shrink-0 gap-1.5" : "shrink-0"}
					aria-label={label}
					title={showLabel ? undefined : label}
				>
					<Icon icon={icon} size={16} />
					{showLabel ? label : null}
				</PopoverTriggerButton>
			</PopoverTrigger>
			<PopoverContent align="start" sideOffset={4} role="dialog" aria-label={label} className="p-3">
				{children}
			</PopoverContent>
		</Popover>
	);
}

/**
 * Teal-outline x-small trigger for the in-toolbar "add portfolio" picker (mirrors the page-header picker).
 * Like the popover buttons, it shows icon + label when the widget is wide enough and collapses to icon-only
 * when narrow — reading the same breakpoint, since it renders inside the toolbar's `Breakpoints` provider.
 */
function AddPortfolioToolbarTrigger({ state, ...props }: DropdownTriggerProps): React.JSX.Element {
	const showLabel = useAtLeast("lg");
	return (
		<Button
			palette="secondary"
			size="x-small"
			className={showLabel ? "shrink-0 gap-1.5" : "shrink-0"}
			aria-label="Add portfolio"
			title={showLabel ? undefined : "Add portfolio"}
			data-qualifier="PortfolioLab/LoadComposition/Button"
			{...props}
		>
			<Icon icon="Outline1" size={16} />
			{showLabel ? "Add portfolio" : null}
		</Button>
	);
}
